/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useMemo } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import {
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material'
import { StateMethods, useHookstate } from '@hookstate/core'
import CloseIcon from '@mui/icons-material/Close'
import { useLang } from '~/hooks/useLang'
import dayjs from 'dayjs'
import { authFetch } from '~/services/fetch'

interface ISubtypeRaw {
  readonly id: number
  readonly name: string
  readonly code?: string
  readonly type_id: number
  readonly type: {
    readonly id: number
    readonly name: string
    readonly code?: string
  }
}

interface ITypeRaw {
  readonly id: number
  readonly name: string
  readonly code?: string
}

interface IGroupedType {
  readonly id: number
  readonly name: string
  readonly code?: string
  readonly subtypes: readonly ISubtypeRaw[]
}

interface ICategory {
  readonly id: number
  readonly name: string
  readonly created_at?: string
  readonly types?: readonly ITypeRaw[]
  readonly subtypes?: readonly ISubtypeRaw[]
}

export interface CategoryViewMoreProps {
  readonly open: StateMethods<boolean>
  readonly itemId: number | null
  readonly handleClose: () => void
}

export default function CategoryViewMore({
  open,
  itemId: categoryId,
  handleClose,
}: CategoryViewMoreProps) {
  const { translate } = useLang()
  const category = useHookstate<ICategory | null>(null)
  const loading = useHookstate<boolean>(false)

  // ignore: category and loading are hookstate objects, adding them causes infinite loops
  useEffect(() => {
    if (categoryId) {
      loading.set(true)
      authFetch({
        url: `/category/${categoryId}`,
        method: 'GET',
      })
        .then((response) => {
          category.set(response.data)
        })
        .finally(() => loading.set(false))
    }
  }, [categoryId])

  const groupedTypes = useMemo((): readonly IGroupedType[] => {
    const data = category.get()
    if (!data) return []

    const subtypes: readonly ISubtypeRaw[] = data.subtypes ?? []
    const map = new Map<number, IGroupedType>()

    subtypes.forEach((subtype) => {
      const typeId = subtype.type.id
      const existing = map.get(typeId)
      if (existing) {
        map.set(typeId, { ...existing, subtypes: [...existing.subtypes, subtype] })
      } else {
        map.set(typeId, {
          id: subtype.type.id,
          name: subtype.type.name,
          code: subtype.type.code,
          subtypes: [subtype],
        })
      }
    })

    // Adiciona tipos diretos sem subtipos associados
    const types: readonly ITypeRaw[] = data.types ?? []
    types.forEach((type) => {
      if (!map.has(type.id)) {
        map.set(type.id, { id: type.id, name: type.name, code: type.code, subtypes: [] })
      }
    })

    return Array.from(map.values())
  }, [category.get()])

  const totalSubtypes = useMemo(
    () => groupedTypes.reduce((acc, group) => acc + group.subtypes.length, 0),
    [groupedTypes]
  )

  const clickOutModalClose = (
    _event: unknown,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick') {
      open.set(false)
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth={'lg'}
      open={open.get()}
      onClose={clickOutModalClose}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {translate('Categoria de Tipo e Subtipo')}
        <IconButton aria-label="close" onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading.get() ? (
          <Grid
            item
            xs={12}
            display={'flex'}
            sx={{ justifyContent: 'center', padding: 4 }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {translate('Name')}
                {':'}
              </Typography>
              <Typography variant="subtitle2">
                {category.get()?.name ?? translate('Uninformed')}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {translate('Criado em')}
                {':'}
              </Typography>
              <Typography variant="subtitle2">
                {category.get()?.created_at
                  ? dayjs(category.get()?.created_at).format('DD/MM/YYYY HH:mm:ss')
                  : translate('Uninformed')}
              </Typography>
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="h6" fontWeight={'bold'} sx={{ mb: 1 }}>
                {translate('Tipo de Ocorrência')}
                {' '}
                <Chip
                  label={`${groupedTypes.length} ${translate('Type')} / ${totalSubtypes} ${translate('Subtype of Occurrence')}`}
                  size="small"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 450, overflow: 'auto' }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="35%">
                        <strong>
                          {translate('Type')}
                        </strong>
                      </TableCell>
                      <TableCell width="65%">
                        <strong>
                          {translate('Subtype of Occurrence')}
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedTypes.length > 0 ? (
                      groupedTypes.map((type: IGroupedType) => (
                        <TableRow key={type.id}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" fontWeight="medium">
                              {type.name}
                            </Typography>
                            {type.code && (
                              <Typography variant="caption" color="text.secondary">
                                {type.code}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {type.subtypes.length > 0 ? (
                              <Grid container spacing={0.5}>
                                {type.subtypes.map((subtype: ISubtypeRaw) => (
                                  <Grid item key={subtype.id}>
                                    <Chip
                                      label={subtype.name}
                                      size="small"
                                      variant="outlined"
                                      title={subtype.code}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {translate('Uninformed')}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          {translate('Uninformed')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )
}
